import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { Box, Typography } from "@mui/material";
import * as echarts from "echarts";
import {
  getMultiDimensionBarTitleSx,
  multiDimensionBarAxisLineStyle,
  multiDimensionBarAxisTickStyle,
  multiDimensionBarChartConfig,
  multiDimensionBarChartStyles,
  multiDimensionBarGridDefaults,
  multiDimensionBarLegendDefaults,
  multiDimensionBarTooltipTextStyle,
} from "./styles/multiDimensionBarChartStyles";

export interface MultiDimensionBarDimension {
  key: string;
  label: string;
  color: string;
}

export interface MultiDimensionBarRow {
  category: string;
  values: Record<string, number>;
}

export interface MultiDimensionBarChartProps {
  title: string;
  titleAlign?: "left" | "center" | "right";
  loading?: boolean;
  dimensions: MultiDimensionBarDimension[];
  rows: MultiDimensionBarRow[];
  height?: number;
  minHeight?: number;
  yAxisMin?: number;
  yAxisMax?: number;
  valueSuffix?: string;
  yAxisValueFormatter?: (value: number) => string;
  tooltipValueFormatter?: (value: number) => string;
  emptyMessage?: string;
  xAxisRotateThreshold?: number;
  xAxisLabelColor?: string;
  xAxisLabelFontSize?: string;
  xAxisLabelFontWeight?: number;
  yAxisLabelColor?: string;
  yAxisLabelFontSize?: string;
  legendTop?: number;
  legendRight?: number;
  stacked?: boolean;
  resizeTrigger?: string | number | boolean | null;
}

export const MultiDimensionBarChart: React.FC<MultiDimensionBarChartProps> = ({
  title,
  titleAlign = "left",
  loading = false,
  dimensions,
  rows,
  height = multiDimensionBarChartConfig.height,
  minHeight = multiDimensionBarChartConfig.minHeight,
  yAxisMin,
  yAxisMax,
  valueSuffix = "%",
  yAxisValueFormatter,
  tooltipValueFormatter,
  emptyMessage = "No data available for the selected filters.",
  xAxisRotateThreshold = multiDimensionBarChartConfig.xAxisRotateThreshold,
  xAxisLabelColor = multiDimensionBarChartConfig.xAxisLabelColor,
  xAxisLabelFontSize = multiDimensionBarChartConfig.xAxisLabelFontSize,
  xAxisLabelFontWeight = multiDimensionBarChartConfig.xAxisLabelFontWeight,
  yAxisLabelColor = multiDimensionBarChartConfig.yAxisLabelColor,
  yAxisLabelFontSize = multiDimensionBarChartConfig.yAxisLabelFontSize,
  legendTop = multiDimensionBarChartConfig.legendTop,
  legendRight = multiDimensionBarChartConfig.legendRight,
  stacked = false,
  resizeTrigger,
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const chartInstanceRef = useRef<echarts.ECharts | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const resizeRafRef = useRef<number | null>(null);
  const hasData = rows.length > 0;

  const scheduleResize = useCallback(() => {
    if (resizeRafRef.current !== null) {
      cancelAnimationFrame(resizeRafRef.current);
    }
    resizeRafRef.current = requestAnimationFrame(() => {
      resizeRafRef.current = null;
      chartInstanceRef.current?.resize();
    });
  }, []);

  const option = useMemo<echarts.EChartsOption>(() => {
    const formatAxisValue =
      yAxisValueFormatter ??
      ((value: number) => `${Number(value).toFixed(1)}${valueSuffix}`);
    const formatTooltipValue =
      tooltipValueFormatter ??
      ((value: number) => `${Number(value).toFixed(2)}${valueSuffix}`);

    return {
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        appendToBody: true,
        confine: false,
        extraCssText: multiDimensionBarChartConfig.tooltipZIndex,
        textStyle: multiDimensionBarTooltipTextStyle,
        formatter: (params: unknown) => {
          const points = Array.isArray(params)
            ? (params as Array<{
                seriesName: string;
                value: number;
                marker: string;
                axisValueLabel: string;
              }>)
            : [];
          if (points.length === 0) {
            return "";
          }
          const titleText = points[0].axisValueLabel;
          const lines = points
            .map(
              (point) =>
                `${point.marker}${point.seriesName}: <strong>${formatTooltipValue(Number(point.value ?? 0))}</strong>`,
            )
            .join("<br/>");
          return `${titleText}<br/>${lines}`;
        },
      },
      legend: {
        ...multiDimensionBarLegendDefaults,
        top: legendTop,
        right: legendRight,
      },
      grid: multiDimensionBarGridDefaults,
      xAxis: {
        type: "category",
        data: rows.map((row) => row.category),
        axisLine: multiDimensionBarAxisLineStyle,
        axisTick: multiDimensionBarAxisTickStyle,
        axisLabel: {
          fontSize: xAxisLabelFontSize,
          color: xAxisLabelColor,
          rotate:
            rows.length > xAxisRotateThreshold
              ? multiDimensionBarChartConfig.xAxisRotateDegrees
              : 0,
          fontWeight: xAxisLabelFontWeight,
        },
      },
      yAxis: {
        type: "value",
        ...(yAxisMin === undefined ? {} : { min: yAxisMin }),
        ...(yAxisMax === undefined ? {} : { max: yAxisMax }),
        axisLine: multiDimensionBarAxisLineStyle,
        axisTick: multiDimensionBarAxisTickStyle,
        splitLine: {
          lineStyle: {
            color: yAxisLabelColor,
            opacity: multiDimensionBarChartConfig.splitLineOpacity,
          },
        },
        axisLabel: {
          formatter: (value: number) => formatAxisValue(Number(value)),
          fontSize: yAxisLabelFontSize,
          color: yAxisLabelColor,
        },
      },
      series: dimensions.map((dimension) => ({
        name: dimension.label,
        type: "bar",
        stack: stacked ? "total" : undefined,
        barMaxWidth: multiDimensionBarChartConfig.barMaxWidth,
        itemStyle: {
          color: dimension.color,
        },
        data: rows.map((row) => Number(row.values[dimension.key] ?? 0)),
      })),
    };
  }, [
    dimensions,
    legendRight,
    legendTop,
    rows,
    stacked,
    valueSuffix,
    xAxisLabelColor,
    xAxisLabelFontSize,
    xAxisLabelFontWeight,
    xAxisRotateThreshold,
    yAxisLabelColor,
    yAxisLabelFontSize,
    yAxisMax,
    yAxisMin,
    yAxisValueFormatter,
    tooltipValueFormatter,
  ]);

  useEffect(() => {
    if (!hasData) {
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
      chartInstanceRef.current?.dispose();
      chartInstanceRef.current = null;
      return undefined;
    }

    const dom = chartRef.current;
    if (!dom) {
      return undefined;
    }

    if (!chartInstanceRef.current) {
      chartInstanceRef.current = echarts.init(dom);
      const resizeObserver = new ResizeObserver(() => {
        scheduleResize();
      });
      resizeObserver.observe(dom);
      resizeObserverRef.current = resizeObserver;
      globalThis.addEventListener("resize", scheduleResize);
    }

    return () => {
      if (resizeRafRef.current !== null) {
        cancelAnimationFrame(resizeRafRef.current);
        resizeRafRef.current = null;
      }
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
      globalThis.removeEventListener("resize", scheduleResize);
      chartInstanceRef.current?.dispose();
      chartInstanceRef.current = null;
    };
  }, [hasData, scheduleResize]);

  useEffect(() => {
    if (!hasData || !chartInstanceRef.current) {
      return;
    }
    chartInstanceRef.current.setOption(option, {
      notMerge: true,
      lazyUpdate: true,
    });
    scheduleResize();
  }, [hasData, option, scheduleResize]);

  useEffect(() => {
    if (resizeTrigger === undefined) return;
    const timer = globalThis.setTimeout(
      () => scheduleResize(),
      multiDimensionBarChartConfig.resizeDelayMs,
    );
    return () => globalThis.clearTimeout(timer);
  }, [resizeTrigger, scheduleResize]);

  if (!hasData && !loading) {
    return (
      <Box sx={multiDimensionBarChartStyles.root}>
        <Typography sx={getMultiDimensionBarTitleSx(titleAlign)}>
          {title}
        </Typography>
        <Box sx={multiDimensionBarChartStyles.emptyStateContainer}>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {emptyMessage}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={multiDimensionBarChartStyles.root}>
      <Typography sx={getMultiDimensionBarTitleSx(titleAlign)}>
        {title}
      </Typography>
      <Box
        sx={multiDimensionBarChartStyles.chartWrapper(
          minHeight,
          loading,
          hasData,
        )}
      >
        {hasData ? (
          <Box
            ref={chartRef}
            sx={multiDimensionBarChartStyles.chartCanvas(height)}
          />
        ) : (
          <Box sx={multiDimensionBarChartStyles.chartPlaceholder(minHeight)} />
        )}
      </Box>
    </Box>
  );
};
